<script setup lang="ts">
  import { computed, watch, provide } from 'vue';
  import { onBeforeRouteLeave } from 'vue-router';
  import { useRoute } from 'vue-router';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import { useFilteredList } from '../model';
  import { FilteredListingTable, DocumentTypeFilterModal, FolderMatricesTable } from '../ui';
  import { useFilteredListHeading } from '../model/useFilteredListHeading';
  import { useVisibleFolderContext } from '../model/useVisibleFolderContext';
  import { CONTAINER_CONTENT_ERROR_MESSAGE } from '../config/directory-container';
  import HomeDefaultView from './HomeDefaultView.vue';
  import { Breadcrumb } from '@/shared/ui';
  import { useTableFilterModal } from '@/shared/lib';
  import { useFilterStore } from '@/entities/Filter';
  import { DOCUMENT_TYPE_ORDER_OPTIONS, FilterOptions } from '@/entities/Document';
  import { useDocumentSelection } from '@/features/Document';

  const route = useRoute();

  const {
    state,
    currentPage,
    totalItems,
    itemsPerPage,
    searchTerm,
    searchType,
    currentFilter,
    hasResults,
    shouldShowNoResults,
    shouldShowFilterNoResults,
    shouldShowEmptyState,
    selectedOrder,
    selectedOrderDirection,
    fetchDocuments,
    retryFetch,
    handleSearch,
    handleSort,
    handleFilter,
    handlePageChange,
    handleQuantityChange,
    clearSearch,
  } = useFilteredList();

  provide(SortOrderInjectionKey, {
    sort: selectedOrder,
    order: selectedOrderDirection,
  });

  const filterStore = useFilterStore();
  const { resetSelection } = useDocumentSelection();

  const documentTypeFilterModal = useTableFilterModal<FilterOptions>(
    DocumentTypeFilterModal,
    filterStore,
    handleFilter,
    () => ({ ...currentFilter.value })
  );

  const isFilterActive = computed(() => documentTypeFilterModal.isFilterActive.value);

  const handleFilterClicked = () => {
    documentTypeFilterModal.openFilterModal();
  };

  onBeforeRouteLeave(() => {
    resetSelection();
    filterStore.clearFilters();
  });

  const currentItemName = computed(() => {
    const param = route.params.itemName;
    if (!param) return '';
    return Array.isArray(param) ? param.join('/') : param;
  });

  const { customFolderTitle, contentKind, matrices } = useVisibleFolderContext(currentItemName);
  // A container holding only subfolders has nothing to list: it keeps the default Home view under
  // its own breadcrumb, and its listing is never requested.
  const showsDefaultView = computed(() => contentKind.value === 'FOLDERS_ONLY');
  const { hasRouteParams, pageTitle } = useFilteredListHeading(
    currentItemName,
    customFolderTitle
  );

  let lastFetchedItemName = '';

  watch(
    [currentItemName, showsDefaultView],
    ([newItemName, defaultView], oldValues) => {
      if (defaultView) {
        // The default view fetches nothing, so coming back to the previous item must fetch again.
        lastFetchedItemName = '';
        return;
      }
      const oldItemName = oldValues?.[0];
      if (newItemName && newItemName !== lastFetchedItemName) {
        if (oldItemName && oldItemName !== newItemName) {
          resetSelection();
          filterStore.clearFilters();
          clearSearch();
        }
        lastFetchedItemName = newItemName;
        fetchDocuments('document', newItemName);
      }
    },
    { immediate: true }
  );
</script>

<template>
  <div class="w-full h-full flex flex-col overflow-auto relative">
    <div class="sticky top-0 z-10">
      <Breadcrumb class="pt-4 pr-6" />
    </div>
    <HomeDefaultView v-if="showsDefaultView" />
    <div
      v-else-if="hasRouteParams"
      class="px-6 w-full flex flex-col"
    >
      <div class="pt-4 pb-6">
        <h1
          class="text-gray-07 text-xl/7.5 font-semibold text-left truncate"
          :title="pageTitle"
        >
          {{ pageTitle }}
        </h1>
      </div>

      <div class="w-full">
        <div
          v-if="state.loadError"
          class="flex flex-col items-start gap-3 py-6 text-left"
          role="alert"
          data-testid="container-content-error"
        >
          <p class="text-sm text-gray-07">
            {{ CONTAINER_CONTENT_ERROR_MESSAGE }}
          </p>
          <button
            type="button"
            class="rounded bg-[var(--theme-secondary)] px-4 py-2 text-sm text-white hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--theme-secondary-ink)]"
            data-testid="container-content-retry"
            @click="retryFetch"
          >
            Tentar novamente
          </button>
        </div>

        <FilteredListingTable
          v-else
          v-model:search-field-id="searchType"
          :should-show-no-results="shouldShowNoResults"
          :should-show-filter-no-results="shouldShowFilterNoResults"
          :has-results="hasResults"
          :should-show-empty-state="shouldShowEmptyState"
          :documents="state.documents"
          :loading="state.loading"
          title="Documentos"
          :total-items="totalItems"
          :current-page="currentPage"
          :items-per-page="itemsPerPage"
          :order-options="DOCUMENT_TYPE_ORDER_OPTIONS"
          :is-filter-active="isFilterActive"
          :search-term="searchTerm"
          @search="handleSearch"
          @update:sort="handleSort"
          @filter-clicked="handleFilterClicked"
          @update:filter="handleFilter"
          @page-changed="handlePageChange"
          @quantity-changed="handleQuantityChange"
        />

        <FolderMatricesTable
          v-if="!state.loadError"
          :matrices="matrices"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
  @media (max-width: 768px) {
    .flex-col {
      gap: 1.5rem;
    }
  }
</style>
